import { useCallback, useRef, useState } from 'react';
import { NativeSyntheticEvent, TextInputSelectionChangeEventData } from 'react-native';
// Deep import: react-native-controlled-mentions@2.2.5 re-exports only a subset
// of its utils from the package root, and `generateValueFromPartsAndChangedText`
// (the routine the field itself runs on every keystroke) is not in it. The
// package declares no `exports` map, so the path resolves; reusing its diff is
// worth the private path because re-encoding plain text to mention syntax by
// hand is exactly where mentions get silently corrupted.
import {
  generateValueFromPartsAndChangedText,
  parseValue,
} from 'react-native-controlled-mentions/dist/utils';

export type TextSelection = { start: number; end: number };

/**
 * The mention field holds two different strings: the ENCODED value
 * (`@[Name](id)`) that is stored and sent, and the PLAIN text the user sees and
 * that TextInput reports selection offsets against. Formatting edits are
 * expressed against the plain text and translated back with the mention
 * library's own diff routine, which is what the field already uses for typing,
 * so mentions can never be split or corrupted by a marker insertion.
 */
const mentionPartTypes = [{ trigger: '@' as const }];

const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max);

/**
 * Selection-aware text editing for the composer's text-style toolbar.
 *
 * Returns the handlers the mention field needs (`selection` /
 * `onSelectionChange`) plus the edit operations the toolbar buttons call. The
 * `selection` it hands back is only set immediately after an edit — restoring
 * the caret — and is cleared as soon as the field reports it back, so the
 * input stays uncontrolled during normal typing where a pinned selection would
 * fight the keyboard.
 */
export const useMentionTextFormatting = (params: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const { value, onChange } = params;

  const selectionRef = useRef<TextSelection>({ start: 0, end: 0 });
  const [pendingSelection, setPendingSelection] = useState<TextSelection | null>(
    null
  );

  const onSelectionChange = useCallback(
    (event: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => {
      selectionRef.current = event.nativeEvent.selection;
      // The field has echoed back the caret we asked for; hand control of the
      // selection back to the keyboard.
      setPendingSelection((current) => (current ? null : current));
    },
    []
  );

  /**
   * Runs `edit` against the plain text at the current selection, re-encodes the
   * result, and pins the caret where the edit asked for it.
   */
  const applyEdit = useCallback(
    (
      edit: (
        plainText: string,
        start: number,
        end: number
      ) => { text: string; selection: TextSelection }
    ) => {
      const { plainText, parts } = parseValue(value, mentionPartTypes);
      const start = clamp(selectionRef.current.start, plainText.length);
      const end = clamp(selectionRef.current.end, plainText.length);

      const result = edit(plainText, Math.min(start, end), Math.max(start, end));

      onChange(
        generateValueFromPartsAndChangedText(parts, plainText, result.text)
      );
      selectionRef.current = result.selection;
      setPendingSelection(result.selection);
    },
    [value, onChange]
  );

  /**
   * Wraps the selection in `open`/`close`. With nothing selected the markers go
   * in empty and the caret lands between them, so the user just keeps typing
   * (no placeholder word to delete first).
   */
  const wrapSelection = useCallback(
    (open: string, close: string) => {
      applyEdit((plainText, start, end) => {
        const selected = plainText.slice(start, end);
        const text =
          plainText.slice(0, start) +
          open +
          selected +
          close +
          plainText.slice(end);
        const innerStart = start + open.length;

        return {
          text,
          selection: { start: innerStart, end: innerStart + selected.length },
        };
      });
    },
    [applyEdit]
  );

  /**
   * Inserts a `[label](url)` link, leaving the caret in the half the user still
   * has to fill: the url when text was selected, the label when it was not.
   */
  const insertLink = useCallback(() => {
    applyEdit((plainText, start, end) => {
      const label = plainText.slice(start, end);
      const inserted = `[${label}]()`;
      const text =
        plainText.slice(0, start) + inserted + plainText.slice(end);
      const caret = label
        ? start + inserted.length - 1 // inside the empty ( )
        : start + 1; // inside the empty [ ]

      return { text, selection: { start: caret, end: caret } };
    });
  }, [applyEdit]);

  /**
   * Prefixes every line the selection touches, for list markers. `marker`
   * receives the line's index so ordered lists can number themselves.
   */
  const prefixLines = useCallback(
    (marker: (index: number) => string) => {
      applyEdit((plainText, start, end) => {
        // lastIndexOf clamps a negative `from` to 0, which would report a
        // leading newline as the block start when the caret is already at 0.
        const blockStart =
          start > 0 ? plainText.lastIndexOf('\n', start - 1) + 1 : 0;
        const nextBreak = plainText.indexOf('\n', end);
        const blockEnd = nextBreak === -1 ? plainText.length : nextBreak;

        const block = plainText
          .slice(blockStart, blockEnd)
          .split('\n')
          .map((line, index) => marker(index) + line)
          .join('\n');

        const text =
          plainText.slice(0, blockStart) + block + plainText.slice(blockEnd);
        const caret = blockStart + block.length;

        return { text, selection: { start: caret, end: caret } };
      });
    },
    [applyEdit]
  );

  return {
    /** Pass to the mention field; only set while restoring a caret. */
    selection: pendingSelection ?? undefined,
    onSelectionChange,
    wrapSelection,
    insertLink,
    prefixLines,
  };
};
